import { useState, useCallback } from 'react';
import type { NwFormSubmissionInput, FormSubmissionResponse } from '@qdf/shared';
import { submitNwForm } from '../api/nw-submissions.js';
import { ApiError } from '../api/client.js';

interface UseNwFormSubmissionReturn {
  submit: (data: NwFormSubmissionInput) => Promise<FormSubmissionResponse>;
  isSubmitting: boolean;
  error: string | null;
  fieldErrors: Record<string, string[]> | null;
}

export function useNwFormSubmission(): UseNwFormSubmissionReturn {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]> | null>(null);

  const submit = useCallback(
    async (data: NwFormSubmissionInput): Promise<FormSubmissionResponse> => {
      setIsSubmitting(true);
      setError(null);
      setFieldErrors(null);

      try {
        return await submitNwForm(data);
      } catch (err) {
        if (err instanceof ApiError) {
          setError(err.body.message);
          if (err.body.details) {
            setFieldErrors(err.body.details);
          }
        } else {
          setError('An unexpected error occurred. Please try again.');
        }
        throw err;
      } finally {
        setIsSubmitting(false);
      }
    },
    [],
  );

  return { submit, isSubmitting, error, fieldErrors };
}
